/**
 * AMCQ panel-graph MST: adjacency on the assignment doc, modules in subcollection.
 * θ routing uses bands easier | medium | harder from cuts stored on the test document.
 */

export const DEFAULT_AMCQ_IRT_CUTS = { cLow: -0.5, cHigh: 0.5 };

/** @typedef {{ cLow: number, cHigh: number }} AmcqIrtCuts */
/** @typedef {{ band: 'easier'|'medium'|'harder', nextModuleId: string }} AmcqRoutingEdge */

/**
 * Merge stored cuts on assignment with defaults (never rely on deploy-time constants only).
 * @param {object} assignment
 * @returns {AmcqIrtCuts}
 */
export function getAmcqIrtCutsFromAssignment(assignment) {
  const raw = assignment?.amcqIrtCuts;
  if (raw && typeof raw === 'object') {
    const cLow = Number(raw.cLow);
    const cHigh = Number(raw.cHigh);
    if (Number.isFinite(cLow) && Number.isFinite(cHigh)) {
      return { cLow, cHigh };
    }
  }
  return { ...DEFAULT_AMCQ_IRT_CUTS };
}

/**
 * @param {number} thetaHat
 * @param {AmcqIrtCuts} cuts
 * @returns {'easier'|'medium'|'harder'}
 */
export function routeBandFromTheta(thetaHat, cuts) {
  const { cLow, cHigh } = cuts;
  if (thetaHat < cLow) return 'easier';
  if (thetaHat > cHigh) return 'harder';
  return 'medium';
}

/**
 * @param {AmcqRoutingEdge[]|undefined|null} edges
 * @param {'easier'|'medium'|'harder'} band
 * @returns {string|null}
 */
export function pickNextModuleId(edges, band) {
  if (!edges || !Array.isArray(edges) || edges.length === 0) return null;
  const match = edges.find((e) => e && e.band === band && e.nextModuleId);
  if (match) return match.nextModuleId;
  const med = edges.find((e) => e && e.band === 'medium' && e.nextModuleId);
  if (med) return med.nextModuleId;
  const first = edges.find((e) => e && e.nextModuleId);
  return first ? first.nextModuleId : null;
}

/**
 * @param {object} assignment
 * @returns {boolean}
 */
export function isPanelGraphAssignment(assignment) {
  if (!assignment || typeof assignment !== 'object') return false;
  return (
    assignment.amcqMode === 'panel_graph' &&
    assignment.amcqPublishStatus === 'published' &&
    typeof assignment.amcqEntryModuleId === 'string' &&
    assignment.amcqEntryModuleId.length > 0 &&
    assignment.amcqRoutingGraph &&
    typeof assignment.amcqRoutingGraph === 'object'
  );
}

/**
 * True if this module has no outgoing edges (end of test after completing it).
 * @param {object} routingGraph
 * @param {string} moduleId
 */
export function isTerminalModule(routingGraph, moduleId) {
  if (!routingGraph || typeof routingGraph !== 'object') return true;
  const edges = routingGraph[moduleId];
  return !edges || !Array.isArray(edges) || edges.length === 0;
}

/**
 * Questions list for IRT (full bank on assignment) or fallback.
 * @param {object} assignment
 * @returns {Array}
 */
export function getAmcqQuestionsForScoring(assignment) {
  if (!assignment) return [];
  if (Array.isArray(assignment.amcqQuestionsFlat) && assignment.amcqQuestionsFlat.length > 0) {
    return assignment.amcqQuestionsFlat;
  }
  return assignment.questions || [];
}
